import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSingleManifesto,
  selectCurrentManifesto,
  deleteManifesto,
} from "@/store/redux/slices/manifesto/manifesto_slice";
import { selectUserInfo } from "@/store/redux/slices/auth/auth_slice";
import { useLoader } from "@/hooks/LoaderProvider";
import Back from "@/components/specific/Back";
import Breadcrumb from "@/components/specific/Breadcrumb";
import HeaderImage from "@/pages/manifesto/components/single_manifesto_details/Header_Image";
import CampaignInfo from "@/pages/manifesto/components/single_manifesto_details/Campaign_Info";
import UserProfileCard from "@/ui/User_Profile_Card";
import ActionButtons from "@/pages/manifesto/components/single_manifesto_details/Action_Button";
import Delete from "@/components/common/alerts/Delete";
import Success from "@/components/common/alerts/Success";
import { fetchOrganizationById } from "@/store/redux/slices/kyc/kyc_setup_slice";
import { calculateKycStatus } from "@/utils/kyc_utils";

const SingleManifesto = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showLoader, hideLoader } = useLoader();
  const manifesto = useSelector(selectCurrentManifesto);
  const userInfo = useSelector(selectUserInfo);
  const organizationData = useSelector(
    (state) => state.organization.organizationData
  );

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [kycStatus, setKycStatus] = useState({
    completionPercentage: 100,
    completedSteps: 3,
  });

  // Dummy data that's not in the API response
  const dummyData = {
    time: "6:30 PM PROMPT", // No known source for where this data is coming from
    publisher: {
      name: userInfo?.fullname,
      avatar: userInfo?.profileImage,
      badgeCount:
        kycStatus.completedSteps > 0 ? kycStatus.completedSteps : null,
    }
  };

  useEffect(() => {
    const fetchManifesto = async () => {
      try {
        showLoader();
        await dispatch(
          fetchSingleManifesto({ key: "_id", value: id })
        ).unwrap();
        await dispatch(fetchOrganizationById());
      } catch (error) {
        console.error("Failed to fetch manifesto:", error);
      } finally {
        hideLoader();
      }
    };

    fetchManifesto();
  }, [dispatch, hideLoader, id, showLoader]);

  useEffect(() => {
    if (organizationData) {
      const status = calculateKycStatus(organizationData);
      setKycStatus(status);
    }
  }, [organizationData]);

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setShowDeleteModal(false);
      showLoader();
      const result = await dispatch(deleteManifesto(manifesto._id)).unwrap();

      if (result?.data?.deletedCount === 1) {
        setShowDeleteModal(false);
        setTimeout(() => {
          setShowDeleteSuccess(true);
        }, 100);
      }
    } catch (error) {
      console.error("Failed to delete manifesto:", error);
    } finally {
      hideLoader();
    }
  };

  const handleDeleteSuccess = () => {
    setShowDeleteSuccess(false);
    navigate("/dashboard/manifesto");
  };

  const handleModify = () => {
    navigate(`/dashboard/manifesto/edit/${id}`);
  };

  if (!manifesto) return null;

  // Format the date from the API response
  const formattedDate = new Date(manifesto.createdAt)
    .toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    })
    .toUpperCase();

  // breadcrumb items
  const items = [
    { title: "Manifesto", link: "/dashboard/manifesto" },
    { title: manifesto?.name || "Manifesto Details", link: null },
  ];

  return (
    <div className="py-6">
      <div className="mb-4">
        <Breadcrumb items={items} />
        <Back text="Back" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 lg:gap-10">
        <div className="col-span-2">
          {/* Keep default image for now since fileUrl might not be a full URL */}
          <HeaderImage image={manifesto?.fileUrl} />

          <div className="mb-4 lg:border-2 border-[#eee] rounded-lg p-4">
            <CampaignInfo
              // Real data from API
              date={formattedDate}
              title={manifesto.name}
              description={manifesto.description}
              // Dummy data
              time={dummyData.time}
              linkedCampaigns={dummyData.linkedCampaigns}
            />
          </div>
        </div>
        <div>
          <div className="lg:border-2 border-[#eee] rounded-lg p-4 mb-5">
            <UserProfileCard
              user={dummyData.publisher}
              completionPercentage={kycStatus.completionPercentage}
              role="Publisher"
            />
          </div>
          <ActionButtons onDelete={handleDelete} onModify={handleModify} />
        </div>
      </div>

      {/* Delete Modal */}
      <Delete
        isVisible={showDeleteModal}
        onProceed={handleConfirmDelete}
        header="Delete Manifesto"
        paragraph="Are you sure you want to delete this manifesto? This action cannot be undone."
        proceedButtonText="Delete"
        cancelButtonText="Cancel"
        onClose={() => setShowDeleteModal(false)}
      />

      {/* Success Dialog */}
      <Success
        isVisible={showDeleteSuccess}
        onClose={() => setShowDeleteSuccess(false)}
        header="Manifesto Deleted"
        paragraph="The manifesto has been successfully deleted."
        buttonText="Done"
        showCancelButton={false}
        onButtonClick={handleDeleteSuccess}
      />
    </div>
  );
};

export default SingleManifesto;
